/**
 * Auksion loti kartasi (ijara-urmon.uz ko'rinishi): sarlavha, narx bloki, taymer,
 * xarita (faqat shu lotning poligoni), to'rt bo'lim (mulk / lot / ijaraga beruvchi /
 * hujjatlar) va g'olib ma'lumoti. Jonli savdo keyingi bosqich.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { auctionApi, lotStatusLabel } from "@/shared/api/auction";
import type { AuctionLotDetail } from "@/shared/api/auction";
import { gisApi } from "@/shared/api/endpoints";
import { useI18n } from "@/shared/i18n";
import { formatDateTime, formatMoney, formatNumber } from "@/shared/lib/format";
import { boundsOfData } from "@/shared/lib/mapGeo";
import type { MapBasemap } from "@/shared/lib/mapGeo";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { useResource } from "@/shared/lib/resource";
import { AsyncBlock } from "@/shared/ui/AsyncBlock";
import { Icon } from "@/shared/ui/Icon";
import { MapView, geoJsonShapes } from "@/shared/ui/MapView";

import { SectionPage } from "./SectionPages";

type Tab = "property" | "lot" | "lessor" | "documents";

const TABS: Array<{ key: Tab; label: string }> = [
  { key: "property", label: "Mulk ma'lumotlari" },
  { key: "lot", label: "Lot ma'lumotlari" },
  { key: "lessor", label: "Ijaraga beruvchi" },
  { key: "documents", label: "Lot hujjatlari" },
];

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="auc-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

const PUBLIC_BASEMAPS: MapBasemap[] = [
  { code: "osm", name: "OpenStreetMap", url_template: "https://tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: "© OpenStreetMap contributors" },
  {
    code: "satellite",
    name: "Sun'iy yo'ldosh",
    url_template: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "© Esri",
  },
];

function LotMap({ lot }: { lot: AuctionLotDetail }) {
  const basemaps = useResource(() => gisApi.basemaps(), []);
  /* `/gis/basemaps/` kirishni talab qiladi — mehmon uchun ochiq asosiy xaritalar ishlatiladi. */
  const ordered = useMemo(() => {
    const rows = basemaps.data ?? [];
    if (!rows.length) return PUBLIC_BASEMAPS;
    return [...rows].sort((a, b) => Number(b.is_default) - Number(a.is_default));
  }, [basemaps.data]);
  const shapes = useMemo(() => geoJsonShapes(lot.geojson ?? null, "#2f7a3f", "lot_number"), [lot.geojson]);
  const bounds = useMemo(() => boundsOfData([], shapes), [shapes]);
  if (!shapes.length || basemaps.loading) return null;
  return (
    <div className="auc-map">
      <MapView
        basemaps={ordered}
        shapes={shapes}
        zoom={13}
        center={lot.center ?? undefined}
        dataBounds={bounds}
        fitTarget={bounds ? { bounds, key: lot.lot_number } : null}
      />
    </div>
  );
}

export function AuctionLotPage() {
  const { lotNumber = "" } = useParams();
  const { locale } = useI18n();
  const [tab, setTab] = useState<Tab>("property");
  const lotRes = useResource(() => auctionApi.lot(lotNumber), [lotNumber]);
  const lot = lotRes.data;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [lotNumber]);

  const done = lot?.status === "completed" || lot?.status === "contract";

  return (
    <SectionPage revealKey={`${lotNumber}-${lot?.status ?? ""}`}>
      <section className="section section-alt auc-detail" aria-labelledby="auc-lot-title">
        <div className="container">
          <nav className="pd-crumbs" aria-label="Yo'l">
            <Link to={SECTION_PATHS.lease}>Ijaraga beriladigan maydonlar</Link>
            <span aria-hidden="true">›</span>
            <span aria-current="page">№ {lotNumber}</span>
          </nav>

          <AsyncBlock
            loading={lotRes.loading}
            error={lotRes.error}
            onRetry={lotRes.reload}
            empty={!lot}
            emptyHint="Lot topilmadi."
            rows={6}
          >
            {lot ? (
              <>
                <div className="auc-lot-head">
                  <div>
                    <span className="eyebrow">{lot.auction_type}</span>
                    <h1 id="auc-lot-title">№ {lot.lot_number} — lot</h1>
                    <p className="auc-lot-farm">
                      {lot.farm_name} · {lot.region_name}
                    </p>
                  </div>
                  <span className={`auc-badge auc-badge-lg ${
                    lot.status === "active" ? "is-active"
                      : lot.status === "completed" ? "is-done"
                      : lot.status === "contract" ? "is-contract" : "is-failed"
                  }`}>
                    {lotStatusLabel(lot.status, lot.status_label)}
                  </span>
                </div>

                <div className="auc-timebar">
                  <span>
                    <Icon name="clock" size={14} /> Boshlanish:{" "}
                    <b>{lot.start_at ? formatDateTime(lot.start_at, locale) : "—"}</b>
                  </span>
                  <span>
                    <Icon name="clock" size={14} /> Yakun:{" "}
                    <b>{lot.end_at ? formatDateTime(lot.end_at, locale) : "—"}</b>
                  </span>
                  <span>
                    <Icon name="users" size={14} /> Ishtirokchilar:{" "}
                    <b className="num">{formatNumber(lot.participants_count, locale)}</b>
                  </span>
                </div>

                <div className="auc-main">
                  <div className="auc-left">
                    <LotMap lot={lot} />

                    <div className="auc-tabs segmented" role="tablist" aria-label="Lot bo'limlari">
                      {TABS.map((item) => (
                        <button
                          key={item.key}
                          type="button"
                          role="tab"
                          aria-selected={tab === item.key}
                          aria-pressed={tab === item.key}
                          onClick={() => setTab(item.key)}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    {tab === "property" ? (
                      <dl className="auc-fields">
                        <Row label="Viloyat" value={lot.region_name} />
                        <Row label="Davlat o'rmon xo'jaligi" value={lot.farm_name} />
                        <Row label="Bo'lim nomi" value={lot.division} />
                        <Row label="Kontur raqami" value={lot.contour} />
                        <Row label="Kvartal" value={lot.quarter} />
                        <Row label="Uchastka raqami" value={lot.plot_label} />
                        <Row label="Kadastr raqami" value={lot.cadastre_number} />
                        <Row label="Maydoni" value={`${formatNumber(Number(lot.area_ha), locale, 2)} ga`} />
                        <Row label="Foydalanish maqsadi" value={lot.purpose} />
                        <Row label="Foydalanish shartlari" value={lot.use_conditions} />
                        <Row label="Ruxsat etilgan qurilish" value={lot.permitted_construction} />
                        <Row label="Cheklovlar" value={lot.restrictions} />
                        <Row label="Ijara muddati" value={lot.lease_term_years ? `${lot.lease_term_years} yil` : ""} />
                      </dl>
                    ) : null}

                    {tab === "lot" ? (
                      <dl className="auc-fields">
                        <Row label="Lot raqami" value={`№ ${lot.lot_number}`} />
                        <Row label="Savdo turi" value={lot.auction_type} />
                        <Row label="O'tkazish usuli" value={lot.method} />
                        <Row label="Auksion boshlangan vaqt" value={lot.start_at ? formatDateTime(lot.start_at, locale) : ""} />
                        <Row label="Auksion yakuni" value={lot.end_at ? formatDateTime(lot.end_at, locale) : ""} />
                        <Row label="Ishtirokchilar soni" value={formatNumber(lot.participants_count, locale)} />
                        <Row label="Holati" value={lot.status_label} />
                      </dl>
                    ) : null}

                    {tab === "lessor" ? (
                      <dl className="auc-fields">
                        <Row label="Mulkchilik shakli" value={lot.lessor_ownership === "individual" ? "Jismoniy shaxs" : "Yuridik shaxs"} />
                        <Row label="Ijaraga beruvchi" value={lot.lessor_name} />
                        <Row label="Xo'jalik nomi" value={lot.farm_name} />
                        <Row label="Manzil" value={lot.lessor_address} />
                        <Row label="Telefon" value={lot.lessor_phone} />
                        <Row label="Elektron pochta" value={lot.lessor_email} />
                      </dl>
                    ) : null}

                    {tab === "documents" ? (
                      lot.documents?.length ? (
                        <ul className="auc-docs">
                          {lot.documents.map((doc, index) => (
                            <li key={index}>
                              <Icon name="file" size={16} />
                              {doc.url ? (
                                <a href={doc.url} target="_blank" rel="noreferrer">{doc.name}</a>
                              ) : (
                                <span>{doc.name}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="market-empty">Hujjat biriktirilmagan.</p>
                      )
                    ) : null}
                  </div>

                  <aside className="auc-side">
                    <div className="auc-pricebox">
                      <div className="auc-price-main">
                        <span>{done ? "Yakuniy narx" : "Joriy narx"}</span>
                        <b className="num">{formatMoney(done ? lot.winner_bid || lot.current_price : lot.current_price, locale)}</b>
                      </div>
                      <dl className="auc-price-rows">
                        <Row label="Boshlang'ich narx" value={formatMoney(lot.starting_price, locale)} />
                        <Row label="Zakalat puli (20%)" value={formatMoney(lot.deposit_amount, locale)} />
                        <Row label="O'sish qadami" value={formatMoney(lot.bid_step, locale)} />
                        {!done ? <Row label="Keyingi eng yaxshi narx" value={formatMoney(lot.next_best_price, locale)} /> : null}
                        {Number(lot.lease_right_amount) > 0 ? (
                          <Row label="Ijara huquqi summasi" value={formatMoney(lot.lease_right_amount, locale)} />
                        ) : null}
                      </dl>
                      {lot.status === "active" ? (
                        <Link className="btn btn-primary btn-block" to={`/kirish?next=${encodeURIComponent(SECTION_PATHS.lease)}&method=oneid`}>
                          <Icon name="lock" size={16} />
                          Savdoda qatnashish
                        </Link>
                      ) : (
                        <p className="auc-closed">Savdo yakunlangan</p>
                      )}
                      <p className="pd-note">
                        <Icon name="info" size={14} />
                        Qatnashish uchun ID.GOV.UZ (E-IMZO) orqali kirish va zakalat to'lash talab etiladi.
                      </p>
                    </div>

                    {lot.winner_name ? (
                      <div className="auc-winner">
                        <h3><Icon name="star" size={16} /> Auksion g'olibi</h3>
                        <Row label="G'olib" value={lot.winner_name} />
                        <Row label="Tasdiqlagan taklif" value={formatMoney(lot.winner_bid, locale)} />
                        <Row label="Tasdiqlagan vaqt" value={lot.winner_confirmed_at ? formatDateTime(lot.winner_confirmed_at, locale) : ""} />
                      </div>
                    ) : null}
                  </aside>
                </div>
              </>
            ) : null}
          </AsyncBlock>
        </div>
      </section>
    </SectionPage>
  );
}
